"use client";

import { useCallback, useEffect, useRef, useState, type CSSProperties, type FocusEvent } from "react";
import { useTranslations } from "next-intl";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import HighlightedTitle from "./HighlightedTitle";
import NetworkPanel from "./network/NetworkPanel";
import { NETWORK_BRANDS } from "./network/brands";
import { useRotatingPanel } from "./network/useRotatingPanel";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";

const DOT_GRID: CSSProperties = {
  backgroundImage: "radial-gradient(rgba(15, 27, 45, 0.12) 1px, transparent 1px)",
  backgroundSize: "24px 24px",
};

const FOCUS_RING =
  "focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-brand-navy";

export default function NetworkSection() {
  const t = useTranslations("network");
  const reducedMotion = usePrefersReducedMotion();
  const sectionRef = useRef<HTMLElement>(null);
  const focusRequestRef = useRef<number | null>(null);
  const [entered, setEntered] = useState(false);
  const [inView, setInView] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting);
        if (entry.isIntersecting) setEntered(true);
      },
      { threshold: 0.2 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const { active, progress, setActive, next, prev } = useRotatingPanel({
    count: NETWORK_BRANDS.length,
    paused: hovered || focused || !inView,
    disabled: reducedMotion,
  });

  const activate = useCallback(
    (index: number, moveFocus: boolean) => {
      focusRequestRef.current = moveFocus ? index : null;
      setActive(index);
    },
    [setActive],
  );

  const consumeFocusRequest = useCallback((index: number) => {
    if (focusRequestRef.current !== index) return false;
    focusRequestRef.current = null;
    return true;
  }, []);

  const handlePanelsBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocused(false);
  };

  const enter = (delayMs: number) => ({
    className: entered
      ? "animate-hero-fade-up motion-reduce:animate-none"
      : "opacity-0 motion-reduce:opacity-100",
    style: { animationDelay: `${delayMs}ms` } as CSSProperties,
  });

  const eyebrow = enter(0);
  const heading = enter(100);
  const controls = enter(200);
  const panels = enter(300);

  return (
    <section ref={sectionRef} id="network" className="w-full overflow-hidden bg-brand-sand" style={DOT_GRID}>
      <div className="mx-auto max-w-[1240px] px-4 py-[72px] sm:px-8">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
          <div className="min-w-0">
            <span
              className={cn(
                "inline-flex items-center gap-2 rounded-full border-[1.5px] border-brand-navy px-4 py-1.5 text-[15px] font-semibold uppercase tracking-wider text-brand-navy",
                eyebrow.className,
              )}
              style={eyebrow.style}
            >
              <span
                aria-hidden="true"
                className="h-2 w-2 animate-network-blink rounded-full bg-brand-accent motion-reduce:animate-none"
              />
              {t("subtitle")}
            </span>
            <h2
              className={cn(
                "mt-5 text-[clamp(36px,4.8vw,64px)] font-bold leading-[1.04] tracking-[-0.02em] text-brand-navy",
                heading.className,
              )}
              style={heading.style}
            >
              <HighlightedTitle title={t("title")} highlight={t("titleHighlight")} />
            </h2>
          </div>

          <div className={cn("flex gap-3", controls.className)} style={controls.style}>
            <button
              type="button"
              onClick={prev}
              aria-label={t("previous")}
              className={cn(
                "flex h-[54px] w-[54px] items-center justify-center rounded-full border-[1.5px] border-brand-navy text-brand-navy transition-transform duration-200 hover:-translate-y-0.5",
                FOCUS_RING,
              )}
            >
              <ArrowLeft className="h-5 w-5" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={next}
              aria-label={t("next")}
              className={cn(
                "flex h-[54px] w-[54px] items-center justify-center rounded-full border-[1.5px] border-brand-navy bg-brand-navy text-brand-cream transition-transform duration-200 hover:-translate-y-0.5",
                FOCUS_RING,
              )}
            >
              <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className={panels.className} style={panels.style}>
          <div
            className="flex flex-col gap-4 min-[760px]:h-[480px] min-[760px]:flex-row"
            onPointerEnter={(e) => e.pointerType === "mouse" && setHovered(true)}
            onPointerLeave={() => setHovered(false)}
            onFocus={() => setFocused(true)}
            onBlur={handlePanelsBlur}
          >
            {NETWORK_BRANDS.map((brand, index) => (
              <NetworkPanel
                key={brand.key}
                brand={brand}
                index={index}
                total={NETWORK_BRANDS.length}
                isActive={index === active}
                reducedMotion={reducedMotion}
                onActivate={activate}
                consumeFocusRequest={consumeFocusRequest}
              />
            ))}
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-1">
              {NETWORK_BRANDS.map((brand, index) => {
                const isActive = index === active;
                return (
                  <button
                    key={brand.key}
                    type="button"
                    onClick={() => activate(index, false)}
                    aria-label={t("show", { brand: brand.name })}
                    aria-current={isActive ? "true" : undefined}
                    className={cn("rounded-full px-1 py-2", FOCUS_RING)}
                  >
                    <span
                      className={cn(
                        "block h-2 overflow-hidden rounded-full bg-brand-dot-track transition-[width] duration-500 motion-reduce:transition-none",
                        isActive ? "w-16" : "w-3",
                      )}
                    >
                      {isActive && (
                        <span
                          className="block h-full w-full origin-left rounded-full bg-brand-navy"
                          style={{ transform: `scaleX(${reducedMotion ? 1 : progress})` }}
                        />
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="text-[15px] text-brand-muted-light">{t("helper")}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
